/**
 * Navigation Zustand store — recents, favourites, palette state, chat state.
 * Persists to localStorage. Ready to migrate to profile service.
 */

import { create } from 'zustand';
import type { RecentPage, FavouritePage, NavCategory, ChatMessage, NavigationAction } from '../types';

// ─── localStorage Keys ───────────────────────────────────────────────────────

const RECENTS_KEY = 'nav_recent_pages';
const FAVOURITES_KEY = 'nav_favourites';
const MAX_RECENTS = 20;

function loadRecents(): RecentPage[] {
    try {
        const raw = localStorage.getItem(RECENTS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

function loadFavourites(): FavouritePage[] {
    try {
        const raw = localStorage.getItem(FAVOURITES_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

function persistRecents(items: RecentPage[]): void {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(items));
}

function persistFavourites(items: FavouritePage[]): void {
    localStorage.setItem(FAVOURITES_KEY, JSON.stringify(items));
}

// ─── Store ───────────────────────────────────────────────────────────────────

export interface NavigationStore {
    // Command Palette
    paletteOpen: boolean;
    openPalette: () => void;
    closePalette: () => void;
    togglePalette: () => void;

    // AI Chat Bar
    chatOpen: boolean;
    chatMessages: ChatMessage[];
    chatInput: string;
    setChatOpen: (open: boolean) => void;
    toggleChat: () => void;
    setChatInput: (input: string) => void;
    addChatMessage: (msg: ChatMessage) => void;
    clearChat: () => void;

    // Recents
    recents: RecentPage[];
    addRecentPage: (page: { path: string; label: string; category: NavCategory }) => void;
    clearRecents: () => void;

    // Favourites
    favourites: FavouritePage[];
    addFavourite: (page: { path: string; label: string; category: NavCategory }) => void;
    removeFavourite: (path: string) => void;
    isFavourite: (path: string) => boolean;
    clearFavourites: () => void;
}

export const useNavigationStore = create<NavigationStore>((set, get) => ({
    // ─── Command Palette ─────────────────────────────────────────────────
    paletteOpen: false,
    openPalette: () => set({ paletteOpen: true }),
    closePalette: () => set({ paletteOpen: false }),
    togglePalette: () => set(s => ({ paletteOpen: !s.paletteOpen })),

    // ─── AI Chat Bar ─────────────────────────────────────────────────────
    chatOpen: false,
    chatMessages: [],
    chatInput: '',
    setChatOpen: (open) => set({ chatOpen: open }),
    toggleChat: () => set(s => ({ chatOpen: !s.chatOpen })),
    setChatInput: (input) => set({ chatInput: input }),
    addChatMessage: (msg) => set(s => ({
        chatMessages: [...s.chatMessages, msg],
    })),
    clearChat: () => set({ chatMessages: [], chatInput: '' }),

    // ─── Recents ─────────────────────────────────────────────────────────
    recents: loadRecents(),

    addRecentPage: ({ path, label, category }) => {
        set(s => {
            const existing = s.recents.find(r => r.path === path);
            let updated: RecentPage[];
            if (existing) {
                updated = s.recents.map(r =>
                    r.path === path
                        ? { ...r, visitCount: r.visitCount + 1, lastVisited: Date.now() }
                        : r
                );
            } else {
                updated = [
                    { path, label, category, visitCount: 1, lastVisited: Date.now() },
                    ...s.recents,
                ];
            }
            // Sort by lastVisited, keep max
            updated.sort((a, b) => b.lastVisited - a.lastVisited);
            updated = updated.slice(0, MAX_RECENTS);
            persistRecents(updated);
            return { recents: updated };
        });
    },

    clearRecents: () => {
        localStorage.removeItem(RECENTS_KEY);
        set({ recents: [] });
    },

    // ─── Favourites ──────────────────────────────────────────────────────
    favourites: loadFavourites(),

    addFavourite: ({ path, label, category }) => {
        set(s => {
            if (s.favourites.some(f => f.path === path)) return s;
            const updated = [...s.favourites, { path, label, category, addedAt: Date.now() }];
            persistFavourites(updated);
            return { favourites: updated };
        });
    },

    removeFavourite: (path) => {
        set(s => {
            const updated = s.favourites.filter(f => f.path !== path);
            persistFavourites(updated);
            return { favourites: updated };
        });
    },

    isFavourite: (path) => get().favourites.some(f => f.path === path),

    clearFavourites: () => {
        localStorage.removeItem(FAVOURITES_KEY);
        set({ favourites: [] });
    },
}));
